/**
 * Reactive auth store. Exposes the current Cognito session (or null) and
 * imperative sign-in/up/out helpers. Subscribed components re-render the
 * moment auth state changes — no router redirect dance, no OAuth callback
 * race condition.
 */

import { create } from 'zustand';
import {
  signIn as amplifySignIn,
  signUp as amplifySignUp,
  confirmSignUp as amplifyConfirmSignUp,
  resendSignUpCode as amplifyResendSignUpCode,
  signOut as amplifySignOut,
  resetPassword as amplifyResetPassword,
  confirmResetPassword as amplifyConfirmResetPassword,
  fetchAuthSession,
  fetchUserAttributes,
} from 'aws-amplify/auth';
import { establishSession } from '../lib/api';

export interface AuthUser {
  sub: string;
  email: string;
  givenName?: string;
  familyName?: string;
  groups: string[];
}

interface AuthState {
  user: AuthUser | null;
  loading: boolean;
  error: string | null;

  init: () => Promise<void>;
  refresh: () => Promise<void>;

  signIn: (email: string, password: string) => Promise<{ ok: boolean; nextStep?: string }>;
  signUp: (email: string, password: string, firstName: string, lastName: string) => Promise<{ ok: boolean; needsConfirm?: boolean; error?: string }>;
  confirmSignUp: (email: string, code: string) => Promise<{ ok: boolean; error?: string }>;
  resendCode: (email: string) => Promise<void>;
  forgotPassword: (email: string) => Promise<{ ok: boolean; error?: string }>;
  confirmForgotPassword: (email: string, code: string, newPassword: string) => Promise<{ ok: boolean; error?: string }>;
  signOut: () => Promise<void>;
  clearError: () => void;
}

function extractUser(session: Awaited<ReturnType<typeof fetchAuthSession>>, attrs: Record<string, string | undefined>): AuthUser | null {
  const idToken = session.tokens?.idToken;
  if (!idToken) return null;
  const groupsClaim = (idToken.payload['cognito:groups'] as string[] | undefined) ?? [];
  return {
    sub: idToken.payload.sub as string,
    email: attrs.email ?? (idToken.payload.email as string) ?? '',
    givenName: attrs.given_name,
    familyName: attrs.family_name,
    groups: groupsClaim,
  };
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  loading: true,
  error: null,

  init: async () => {
    try {
      const session = await fetchAuthSession();
      if (!session.tokens) { set({ user: null, loading: false }); return; }
      const attrs = await fetchUserAttributes().catch(() => ({} as any));
      set({ user: extractUser(session, attrs), loading: false });
      // Ensure session token exists (first load or returning user)
      if (!localStorage.getItem('squishy_session_token')) {
        await establishSession().catch(() => {});
      }
    } catch {
      set({ user: null, loading: false });
    }
  },

  refresh: async () => {
    try {
      const session = await fetchAuthSession({ forceRefresh: true });
      const attrs = await fetchUserAttributes().catch(() => ({} as any));
      set({ user: extractUser(session, attrs) });
    } catch {
      set({ user: null });
    }
  },

  signIn: async (email, password) => {
    set({ error: null, loading: true });
    try {
      const result = await amplifySignIn({ username: email, password });
      if (result.isSignedIn) {
        await get().refresh();
        // Establish single-session token (invalidates other devices)
        await establishSession().catch(() => {});
        set({ loading: false });
        return { ok: true };
      }
      set({ loading: false });
      return { ok: false, nextStep: result.nextStep?.signInStep };
    } catch (e: any) {
      set({ error: friendlyError(e), loading: false });
      return { ok: false };
    }
  },

  signUp: async (email, password, firstName, lastName) => {
    set({ error: null, loading: true });
    try {
      const result = await amplifySignUp({
        username: email,
        password,
        options: {
          userAttributes: { email, given_name: firstName, family_name: lastName },
        },
      });
      set({ loading: false });
      return { ok: true, needsConfirm: !result.isSignUpComplete };
    } catch (e: any) {
      const msg = friendlyError(e);
      set({ error: msg, loading: false });
      return { ok: false, error: msg };
    }
  },

  confirmSignUp: async (email, code) => {
    set({ error: null, loading: true });
    try {
      await amplifyConfirmSignUp({ username: email, confirmationCode: code });
      set({ loading: false });
      return { ok: true };
    } catch (e: any) {
      const msg = friendlyError(e);
      set({ error: msg, loading: false });
      return { ok: false, error: msg };
    }
  },

  resendCode: async (email) => {
    await amplifyResendSignUpCode({ username: email });
  },

  forgotPassword: async (email) => {
    set({ error: null });
    try {
      await amplifyResetPassword({ username: email });
      return { ok: true };
    } catch (e: any) {
      const msg = friendlyError(e);
      set({ error: msg });
      return { ok: false, error: msg };
    }
  },

  confirmForgotPassword: async (email, code, newPassword) => {
    set({ error: null });
    try {
      await amplifyConfirmResetPassword({ username: email, confirmationCode: code, newPassword });
      return { ok: true };
    } catch (e: any) {
      const msg = friendlyError(e);
      set({ error: msg });
      return { ok: false, error: msg };
    }
  },

  signOut: async () => {
    try { await amplifySignOut(); } finally {
      localStorage.removeItem('squishy_session_token');
      set({ user: null });
    }
  },

  clearError: () => set({ error: null }),
}));

function friendlyError(e: any): string {
  const code = e?.name ?? e?.code ?? '';
  const msg = e?.message ?? String(e);
  switch (code) {
    case 'NotAuthorizedException': return 'Incorrect email or password.';
    case 'UserNotFoundException':  return 'No account found with that email.';
    case 'UserNotConfirmedException': return 'Please verify your email first — check your inbox for the code.';
    case 'CodeMismatchException':  return 'That verification code is incorrect.';
    case 'ExpiredCodeException':   return 'That code has expired. Request a new one.';
    case 'UsernameExistsException':return 'An account with this email already exists.';
    case 'InvalidPasswordException': return 'Password must be at least 10 characters with upper, lower, and number.';
    case 'LimitExceededException': return 'Too many attempts. Please wait a few minutes and try again.';
    default:                       return msg;
  }
}
